import { useState } from "react";
import { motion } from "motion/react";
import { CameraCapture, type Capture } from "./CameraCapture.js";
import { uploadMedia } from "../media/upload.js";
import type { GuestIdentity } from "./guest.api.js";

type Phase = "capture" | "compose" | "sending" | "done";

export function CaptureView({
  eventId,
  token,
  identity,
  onSent,
}: {
  eventId: string;
  token: string | undefined;
  identity: GuestIdentity;
  onSent: () => void;
}) {
  const [phase, setPhase] = useState<Phase>("capture");
  const [capture, setCapture] = useState<Capture | null>(null);
  const [note, setNote] = useState("");
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState("");

  async function send() {
    if (!capture || !token) return;
    setPhase("sending");
    setProgress(0);
    setErr("");
    try {
      await uploadMedia({
        eventId,
        token,
        file: capture.blob,
        kind: capture.kind,
        contentType: capture.contentType,
        name: identity.name,
        email: identity.email,
        note: note.trim(),
        onProgress: setProgress,
      });
      setPhase("done");
      onSent();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Échec de l'envoi");
      setPhase("compose");
    }
  }

  function again() {
    setCapture(null);
    setNote("");
    setPhase("capture");
  }

  if (phase === "capture") {
    return (
      <div className="relative h-full w-full">
        <CameraCapture
          onCapture={(c) => {
            setCapture(c);
            setPhase("compose");
          }}
        />
      </div>
    );
  }

  return (
    <div className="relative flex h-full flex-col bg-black">
      <div className="min-h-0 flex-1">
        {capture?.kind === "image" ? (
          <img src={capture.previewUrl} alt="" className="h-full w-full object-contain" />
        ) : (
          <video src={capture?.previewUrl} controls playsInline className="h-full w-full object-contain" />
        )}
      </div>

      {phase === "done" ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="safe-bottom flex flex-col items-center gap-3 px-6 py-6 text-white"
        >
          <span className="text-3xl">🎉</span>
          <p className="text-center text-sm text-white/80">
            Envoyé ! Surprise pour les mariés jusqu'à l'ouverture de la galerie.
          </p>
          <button
            onClick={again}
            className="rounded-full px-6 py-3 text-sm font-semibold"
            style={{ background: "var(--theme-primary)", color: "var(--theme-primary-text)" }}
          >
            En envoyer un autre
          </button>
        </motion.div>
      ) : (
        <div className="safe-bottom flex flex-col gap-3 bg-gradient-to-t from-black/70 to-transparent px-5 pb-5 pt-8">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Un petit mot pour les mariés (facultatif)"
            className="w-full rounded-xl border-0 bg-white/15 px-4 py-3 text-base text-white placeholder:text-white/50 backdrop-blur focus:outline-none"
          />
          {err && <p className="text-center text-xs text-rose-300">{err}</p>}
          <div className="flex items-center gap-3">
            <button
              onClick={again}
              disabled={phase === "sending"}
              className="rounded-full bg-white/15 px-5 py-3 text-sm font-medium text-white backdrop-blur disabled:opacity-40"
            >
              Refaire
            </button>
            <button
              onClick={send}
              disabled={phase === "sending"}
              className="relative flex-1 overflow-hidden rounded-full py-3 text-sm font-semibold disabled:opacity-80"
              style={{ background: "var(--theme-primary)", color: "var(--theme-primary-text)" }}
            >
              {phase === "sending" && (
                <span
                  className="absolute inset-y-0 left-0 bg-black/20"
                  style={{ width: `${progress}%` }}
                />
              )}
              <span className="relative">
                {phase === "sending" ? `Envoi… ${progress}%` : "Envoyer"}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
